import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';

import appContext from '../../constants/appContext';
import EventDetailsBottom from '../EventDetailsBottom';
import EventDetailsContent from '../EventDetailsContent';
import EventDetailsHeader from '../EventDetailsHeader';
import EventShape from '../types/eventShape';
import eventTypes from '../../constants/eventTypes';
import PoweredBy from '../PoweredBy';
import Schema from '../../utils/schema';
import VenueShape from '../types/venue';

import './EventDetailsPage.scss';
import containerQueryProps from '../../props/containerQueryProps';
import { sanitizeString } from '../../utils/sanitizeString';

const { GENERAL_ADMISSION, SEATED } = eventTypes;

const EventDetailsPage = ({
  $rootScope,
  Analytics,
  city,
  containerQuery,
  context,
  copyPosition,
  // `date` is the selected event's own date.
  date,
  // `day` is an optional filter for same-day upcoming events.
  day,
  event,
  eventSlug,
  fetchEventDetails,
  isOnline,
  isMobile,
  login,
  needToKnowCopy,
  noIndex,
  notifyService,
  onTicketQuantitySelect,
  enableBookNow,
  previewMode,
  sameDay,
  safetyMeasures,
  safetyMeasureUrl,
  seats,
  selectResy,
  showAbout,
  showBookNowBtn,
  showBottomSection,
  showFooter,
  showNotify,
  tableConfigId,
  ticketTypes,
  type,
  userIsGda,
  venue,
  venueSlug,
}) => {
  const isPageViewEventFired = useRef(false);
  const schemaEvent = {
    date: event?.date,
    description: event?.description,
    end_time: event?.endTime,
    image_preview: event?.images?.[0],
    name: event?.title,
    packages: event?.packages,
    price_summary: event?.priceSummary,
    time: event?.time,
    venue,
  };

  useEffect(() => {
    if (event?.title && !isPageViewEventFired.current) {
      isPageViewEventFired.current = true;
      if (event?.isGlobalDiningAccess) {
        Analytics.event('view gda page', {
          context: 'event page',
        });
      }
    }
  }, [Analytics, event?.isGlobalDiningAccess, event?.title]);

  return (
    <div className="EventDetailsPage">
      <script type="application/ld+json">
        {sanitizeString(JSON.stringify(Schema(schemaEvent)))}
      </script>

      <Helmet>
        {noIndex && (
          <meta name="robots" content="noindex" />
        )}
      </Helmet>
      <EventDetailsHeader
        containerQuery={containerQuery}
        context={context}
        event={event}
        isOnline={isOnline}
        locationSlug={event?.venueData?.location?.url_slug}
        showBookNowBtn={showBookNowBtn}
        tableConfigId={tableConfigId}
        venueSlug={venueSlug}
        isMobile={isMobile}
      />
      <EventDetailsContent
        $rootScope={$rootScope}
        Analytics={Analytics}
        city={city}
        containerQuery={containerQuery}
        context={context}
        copyPosition={copyPosition}
        date={date}
        event={event}
        eventSlug={eventSlug}
        fetchEventDetails={fetchEventDetails}
        login={login}
        needToKnowCopy={needToKnowCopy}
        notifyService={notifyService}
        onTicketQuantitySelect={onTicketQuantitySelect}
        enableBookNow={enableBookNow}
        previewMode={previewMode}
        safetyMeasures={safetyMeasures}
        safetyMeasureUrl={safetyMeasureUrl}
        seats={seats}
        selectResy={selectResy}
        showAbout={showAbout}
        showNotify={showNotify}
        ticketTypes={ticketTypes}
        type={type}
        userIsGda={userIsGda}
        venue={venue}
      />
      {showBottomSection && (
        <EventDetailsBottom
          $rootScope={$rootScope}
          Analytics={Analytics}
          containerQuery={containerQuery}
          context={context}
          event={event}
          eventId={event.eventId}
          day={day}
          isMobile={isMobile}
          sameDay={sameDay}
          venue={venue}
        />
      )}
      {showFooter && (
        <PoweredBy />
      )}
    </div>
  );
};

EventDetailsPage.propTypes = {
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func,
    previewMode: PropTypes.bool,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  city: PropTypes.string, // eg 'ny'
  containerQuery: containerQueryProps,
  context: PropTypes.oneOf([
    appContext.CONSUMER,
    appContext.WIDGET,
  ]),
  copyPosition: PropTypes.oneOf(['top', 'bottom']),
  date: PropTypes.string.isRequired,
  day: PropTypes.string,
  event: EventShape.isRequired,
  eventSlug: PropTypes.string,
  fetchEventDetails: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  isOnline: PropTypes.bool,
  onTicketQuantitySelect: PropTypes.func.isRequired,
  enableBookNow: PropTypes.bool.isRequired,
  needToKnowCopy: PropTypes.string,
  noIndex: PropTypes.bool,
  notifyService: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  previewMode: PropTypes.bool,
  safetyMeasures: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      active: PropTypes.bool.isRequired,
      id: PropTypes.number.isRequired,
      order_num: PropTypes.number.isRequired,
      safety_category_id: PropTypes.number.isRequired,
      value: PropTypes.string.isRequired,
    })),
    order_num: PropTypes.number.isRequired,
    value: PropTypes.string.isRequired,
  })),
  safetyMeasureUrl: PropTypes.string,
  sameDay: PropTypes.bool,
  seats: PropTypes.number,
  selectResy: PropTypes.func.isRequired,
  showAbout: PropTypes.bool,
  showBookNowBtn: PropTypes.bool,
  showFooter: PropTypes.bool,
  showBottomSection: PropTypes.bool,
  showNotify: PropTypes.bool.isRequired,
  tableConfigId: PropTypes.string,
  ticketTypes: PropTypes.arrayOf(PropTypes.shape({
    availableQuantity: PropTypes.number,
    description: PropTypes.string,
    isGda: PropTypes.bool,
    isPresaleSoldOut: PropTypes.bool,
    price: PropTypes.string,
    priceNumber: PropTypes.number,
    slots: PropTypes.arrayOf(PropTypes.shape({})),
    soldOut: PropTypes.bool,
    title: PropTypes.string,
  })),
  type: PropTypes.oneOf([GENERAL_ADMISSION, SEATED]),
  userIsGda: PropTypes.bool,
  login: PropTypes.func,
  venue: VenueShape.isRequired,
  venueSlug: PropTypes.string,
};

EventDetailsPage.defaultProps = {
  city: undefined,
  containerQuery: {},
  context: undefined,
  copyPosition: undefined,
  day: undefined,
  eventSlug: undefined,
  isMobile: false,
  isOnline: false,
  login: null,
  needToKnowCopy: '',
  noIndex: false,
  notifyService: undefined,
  previewMode: false,
  safetyMeasures: undefined,
  safetyMeasureUrl: undefined,
  sameDay: undefined,
  seats: null,
  showAbout: true,
  showBookNowBtn: true,
  showFooter: false,
  showBottomSection: true,
  tableConfigId: undefined,
  ticketTypes: [],
  type: undefined,
  userIsGda: false,
  venueSlug: undefined,
};

EventDetailsPage.displayName = 'EventDetailsPage';
export default EventDetailsPage;
